<template>
  <el-row type="flex" class="mx-4 mb-4 mt-0 flex-row flex-wrap">
    <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
    <el-button
      target="_blank"
      :type="buttonOutline"
      :class="{ 'blue-button': buttonOutline === 'primary' }"
      @click="openScribe"
    >
      Open Scribe
    </el-button>
  </el-row>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'
import { ElNotification } from 'element-plus'
import { useMainStore } from '@/stores/main'

defineOptions({
  name: 'MessageScribe',
})

const props = defineProps<{
  buttonOutline?: string
}>()

const store = useMainStore()

const scriptURL = ref('')
const observer = ref<MutationObserver | null>(null)

const heidiOptions = ref({
  token: '',
  target: '#heidi',
  display: {
    position: 'bottom-left',
  },
  onInit: onHeidiInit,
  onReady: onHeidiReady,
})

async function openScribe() {
  if (heidiOptions.value.token === '') {
    await getToken()
  }

  loadWidget()
}

async function getToken() {
  try {
    const result = await store.getScribeToken()
    heidiOptions.value.token = result.token
    scriptURL.value = result.scriptURL
  } catch (err) {
    ElNotification.error({
      title: 'Error',
      position: 'bottom-right',
      message: "Error getting scribe token. Please click 'Open Scribe' again.",
    })
  }
}

function loadWidget() {
  if (document.querySelector("script[src*='heidi.js']")) {
    openHeidi()
    return
  }

  const script = document.createElement('script')
  script.async = true
  script.src = scriptURL.value
  document.head.appendChild(script)

  script.addEventListener('load', () => {
    // eslint-disable-next-line
    new (window as any).Heidi(heidiOptions.value)
    openHeidi()
  })
}

function openHeidi() {
  // eslint-disable-next-line
  ;(window as any).Heidi.open()
}

function closeHeidi() {
  // eslint-disable-next-line
  ;(window as any).Heidi.close()
}

function onHeidiInit() {
  console.log('Heidi initialized')
}

function onHeidiReady() {
  // eslint-disable-next-line
  ;(window as any).Heidi.onPushData(() => {
    ElNotification.info({
      title: 'Info',
      position: 'bottom-right',
      message: 'Your note has been successfully saved!',
    })
  })

  // eslint-disable-next-line
  ;(window as any).Heidi.onSessionStarted(() => {
    setTimeout(() => {
      const iframe = document.querySelector('.heidi-iframe') as HTMLIFrameElement | null
      if (!iframe) return

      const iframeDocument = iframe.contentDocument
      if (!iframeDocument) return

      const leftHeader = iframeDocument.getElementById('widget-header-left-section')
      const rightHeader = iframeDocument.getElementById('widget-header-right-section')

      const logoSVG = leftHeader?.querySelector('svg')
      if (logoSVG) {
        logoSVG.style.display = 'none'
      }

      const buttons = rightHeader?.querySelectorAll('button')

      observer.value = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-state') {
            if (buttons?.[1]) {
              const buttonClosed = buttons[1].getAttribute('data-state') === 'closed'
              if (!buttonClosed) {
                const tooltipElement = buttons[1].nextElementSibling
                const tooltipDiv = tooltipElement?.querySelector('div')
                if (tooltipDiv) {
                  tooltipDiv.childNodes[0].nodeValue = 'Close'
                }

                const tooltipSpan = tooltipElement?.querySelectorAll('span')
                if (tooltipSpan?.[1]) {
                  tooltipSpan[1].textContent = 'Close'
                }
              }
            }

            const dropdownMenu = iframeDocument.querySelector('[role="menu"]')
            if (dropdownMenu) {
              const menuItems = dropdownMenu.querySelectorAll('[role="menuitem"]')
              if (menuItems.length === 3) {
                ;(menuItems[2] as HTMLElement).style.display = 'none'
              }
            }
          }
        })
      })

      observer.value.observe(iframeDocument, {
        childList: true,
        subtree: true,
        attributes: true,
      })
    }, 1000)
  })

  // eslint-disable-next-line
  ;(window as any).Heidi.onTokenExpired(() => {
    getToken().then(() => {
      // eslint-disable-next-line
      ;(window as any).Heidi.setToken(heidiOptions.value.token)
    })
  })
}

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})
</script>

<style lang="scss" scoped>
.el-button {
  margin: 0 5px 5px 0;
  border-radius: 25px;
  font-family: var(--keyreply-font-family);

  &.active {
    background: transparent;
  }
}

.ui-beta {
  .el-button {
    margin: 0 5px 5px 0;
    border-radius: 8px;
    white-space: normal;
    text-align: left;

    &.blue-button {
      color: #4e6cce;
      background: #dce2f5;
      border-color: #4e6cce;

      &:hover {
        background: #4e6cce;
        color: #fff;
      }
    }
  }
}
</style>
